import loadable from '@loadable/component';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { useCallback, useEffect, useReducer } from 'react';
import { withTranslation } from 'react-i18next';

import { useFormatCurrency } from '@krea/common/hooks';
import { settings } from '@krea/common/settings';
import { Error } from '@krea/common/shared-components/fields/error';
import Fieldset from '@krea/common/shared-components/fields/fieldset';
import TextInput from '@krea/common/shared-components/fields/input';
import { Select } from '@krea/common/shared-components/fields/select/';
import TextArea from '@krea/common/shared-components/fields/textarea';
import { usePostFieldRequest } from '@krea/common/store/field-requests/hooks/mutations';
import { useGetOneFieldRequest } from '@krea/common/store/field-requests/hooks/queries';

import FieldRequestPreviewHeader from '../FieldRequestPreviewHeader';
import FieldRequestSaved from '../FieldRequestSaved';

const FreeTextParser = loadable(
  () => import('@krea/common/shared-components/freetext-parser'),
);

const dueDateOptions = [14, 21, 30, 45, 60, 90, 120];

const UnpaidInvoicesForm = ({ fieldRequestID, isPreviewMode, t }) => {
  const [formattedCurrencyValue, currencyValue] = useFormatCurrency({
    currency: settings.currency,
  });
  const { data: { displayName, submittedData, field } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);

  const errors = error?.data?.errors || [];

  const {
    estimated_invoice_amount,
    number_of_invoices,
    average_payment_time_days,
    invoices_description,
  } = submittedData;

  function reducer(state, action) {
    switch (action.type) {
      case 'touched':
        return { ...state, touched: action.payload };

      case 'estimated_invoice_amount':
        return { ...state, estimated_invoice_amount: action.payload };

      case 'number_of_invoices':
        return { ...state, number_of_invoices: action.payload };

      case 'average_payment_time_days':
        return { ...state, average_payment_time_days: action.payload };

      case 'invoices_description':
        return { ...state, invoices_description: action.payload };

      default:
        throw new Error();
    }
  }

  const [state, setState] = useReducer(reducer, {
    touched: false,
    estimated_invoice_amount: estimated_invoice_amount || '',
    number_of_invoices: number_of_invoices || '',
    average_payment_time_days: average_payment_time_days || 30,
    invoices_description: invoices_description || '',
  });

  const createFormData = (value) => {
    const formData = new FormData();

    formData.append(
      'estimated_invoice_amount',
      value.estimated_invoice_amount || '',
    );
    formData.append('number_of_invoices', value.number_of_invoices || '');
    formData.append(
      'average_payment_time_days',
      value.average_payment_time_days || '',
    );
    formData.append('invoices_description', value.invoices_description || '');

    return formData;
  };

  const onBlur = (state) => {
    const { touched } = state;
    const value = state;
    const formData = createFormData(value);

    if (touched) {
      postFieldRequest(formData);

      if (touched) {
        setState({ type: 'touched', payload: false });
      }
    }
  };

  const onChange = ({ target }) => {
    let parsedValue = target.value;

    if (target.name === 'estimated_invoice_amount') {
      const parsed = parseInt(target.value.replace(/\s+/g, ''), 10);
      parsedValue = Number.isInteger(parsed) ? parsed : '';
    }

    setState({ type: `${target.name}`, payload: parsedValue });
    setState({ type: 'touched', payload: state.value !== parsedValue });
  };

  const debouncedFunc = debounce((state) => onBlur(state), 2500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const formClasses = classnames('input-form', {
    'input-form-padded': isPreviewMode,
  });

  return (
    <div className={formClasses}>
      {isPreviewMode ? (
        <>
          <FieldRequestPreviewHeader
            label={displayName}
            value={
              <>
                {state.estimated_invoice_amount &&
                  formattedCurrencyValue(state.estimated_invoice_amount)}
              </>
            }
          />
          {state.estimated_invoice_amount && (
            <>
              <FieldRequestPreviewHeader
                subheader
                label={t('iaq.unpaidInvoices.numberOfInvoices.question')}
                value={`${number_of_invoices} st`}
              />

              <FieldRequestPreviewHeader
                subheader
                label={t('iaq.unpaidInvoices.averagePaymentTimeDays.question')}
                value={`${average_payment_time_days} dagar`}
              />

              {!!invoices_description && (
                <FieldRequestPreviewHeader
                  subheader
                  label={t('iaq.unpaidInvoices.invoicesDescription.preview')}
                  value={<FreeTextParser text={invoices_description} />}
                />
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Fieldset className="mb-5">
            <TextInput
              type="text"
              placeholder={t('iaq.unpaidInvoices.invoiceValue.placeholder')}
              textAppend={currencyValue}
              label={t('iaq.unpaidInvoices.invoiceValue.question')}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="11"
              id="estimated_invoice_amount"
              name="estimated_invoice_amount"
              value={state.estimated_invoice_amount?.toLocaleString('sv') || ''}
              onChange={onChange}
              onBlur={() => onBlur(state)}
            />

            <TextInput
              type="text"
              placeholder={t('iaq.unpaidInvoices.numberOfInvoices.placeholder')}
              label={t('iaq.unpaidInvoices.numberOfInvoices.question')}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="15"
              id="number_of_invoices"
              name="number_of_invoices"
              value={state.number_of_invoices || ''}
              onChange={onChange}
              onBlur={() => onBlur(state)}
            />

            <Select
              label={t('iaq.unpaidInvoices.averagePaymentTimeDays.question')}
              id="average_payment_time_days"
              name="average_payment_time_days"
              value={state.average_payment_time_days || 30}
              onChange={onChange}
              onBlur={() => onBlur(state)}
            >
              <option value="" disabled hidden>
                {t('iaq.unpaidInvoices.averagePaymentTimeDays.choose')}
              </option>
              {dueDateOptions.map((item, index) => (
                <option key={index} value={item}>
                  {t('iaq.unpaidInvoices.dueDateOptions', { days: item })}
                </option>
              ))}
            </Select>

            <TextArea
              id="invoices_description"
              name="invoices_description"
              label={t('iaq.unpaidInvoices.invoicesDescription.question')}
              placeholder={t(
                'iaq.unpaidInvoices.invoicesDescription.placeholder',
              )}
              value={state.invoices_description || ''}
              onChange={onChange}
              onBlur={() => onBlur(state)}
            />
          </Fieldset>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />

          {errors.map((error, index) => (
            <Error className="mt-3" key={index}>
              {error.message}
            </Error>
          ))}
        </>
      )}
    </div>
  );
};

export default withTranslation()(UnpaidInvoicesForm);
